// Clubs (teams): a player belongs to at most one club. A club has a name, a short tag, a kit and an invitation code.
// The kit is made of ordinary cosmetic creations (ADR 0017): a jersey, and optionally a helmet, a frame and wheels,
// each picked from the store or made in the workshop by the owner or a captain. The owner runs it, captains help,
// members ride.
import { z } from 'zod/v4';
import type { Look } from './index.js';
import { CosmeticContent, CreationId, type CosmeticSlot } from './creations.js';

const Hex = z.string().regex(/^#[0-9a-f]{6}$/i, 'Colour #rrggbb expected');

export const CLUB_LIMITS = { members: 50, name: [3, 40] as const, tag: [2, 4] as const, description: 300 } as const;
export const CLUB_ROLES = ['owner', 'captain', 'member'] as const;
export type ClubRole = (typeof CLUB_ROLES)[number];
/** `open`: anyone can join from the search. `invite`: the code (or its link) is needed. */
export const CLUB_VISIBILITIES = ['open', 'invite'] as const;
export type ClubVisibility = (typeof CLUB_VISIBILITIES)[number];
/** Patterns of the quick kit: each is drawn with the club's two colours into a jersey creation (packages/creations club.ts). */
export const CLUB_PATTERNS = ['plain', 'gradient', 'stripes', 'dots', 'checker', 'chevrons', 'band'] as const;
export type ClubPattern = (typeof CLUB_PATTERNS)[number];

export const ClubName = z.string().trim().min(CLUB_LIMITS.name[0]).max(CLUB_LIMITS.name[1]);
/** Tag: 2 to 4 letters or digits, stored upper case, shown next to the members' names. */
export const ClubTag = z.string().trim().toUpperCase().regex(/^[0-9A-Z]{2,4}$/, 'Tag: 2 to 4 letters or digits');
/**
 * Quick kit: a pattern and two colours. The server turns it into a jersey creation owned by the requester and makes it
 * the club's jersey, so a club gets a kit in one step and can still open it in the workshop afterwards.
 */
export const ClubJersey = z.object({
  pattern: z.enum(CLUB_PATTERNS).default('plain'),
  /** Main colour of the kit (also the team colour of a member who wears it). */
  color: Hex,
  /** Second colour: stripes, dots, bands. */
  accent: Hex,
  /** Prints the tag on the chest. */
  tag: z.boolean().default(true),
});
export type ClubJersey = z.infer<typeof ClubJersey>;
/** Kit by creation ids, per slot: a cosmetic creation of that slot the requester may use; `null` takes the piece off. */
export const ClubKitBody = z.strictObject({ jersey: CreationId.nullable().optional(), frame: CreationId.nullable().optional(), helmet: CreationId.nullable().optional(), wheels: CreationId.nullable().optional() });
export type ClubKitBody = z.infer<typeof ClubKitBody>;

/** Shapes without default values: in zod v4, `.partial()` on a schema with defaults would fill them in and overwrite the club. */
const clubShape = { name: ClubName, tag: ClubTag, description: z.string().trim().max(CLUB_LIMITS.description), visibility: z.enum(CLUB_VISIBILITIES), quickKit: ClubJersey, kit: ClubKitBody };
export const ClubCreateBody = z.object({ ...clubShape, description: clubShape.description.default(''), visibility: clubShape.visibility.default('invite'), quickKit: ClubJersey.optional(), kit: ClubKitBody.optional() });
export type ClubCreateBody = z.input<typeof ClubCreateBody>;
export const ClubPatchBody = z.object(clubShape).partial();
export type ClubPatchBody = z.input<typeof ClubPatchBody>;
export const ClubIdParam = z.object({ id: z.string().uuid('Invalid identifier') });
export const ClubMemberParam = z.object({ id: z.string().uuid('Invalid identifier'), userId: z.string().uuid('Invalid identifier') });
export const ClubCode = z.string().trim().toUpperCase().regex(/^[0-9A-Z]{6,10}$/, 'Invalid club code');
export const ClubCodeParam = z.object({ code: ClubCode });
/** Join by code (any club) or by id (open clubs only). */
export const ClubJoinBody = z.union([z.object({ code: ClubCode }), z.object({ clubId: z.string().uuid() })]);
export const ClubRoleBody = z.object({ role: z.enum(CLUB_ROLES) });
export const ClubSearchQuery = z.object({ q: z.string().trim().min(2).max(40) });

/** A piece of the kit as everyone sees it: the creation, its author and the content members embed in their outfit. */
export interface ClubKitPiece { id: string; name: string; author: { id: string; name: string } | null; content: CosmeticContent }
export type ClubKit = Partial<Record<CosmeticSlot, ClubKitPiece>>;
/** Zod view of a kit piece (the game server validates nothing of it: the kit travels embedded in `look.wear`). */
export const ClubKitPieceSchema = z.object({ id: CreationId, name: z.string().max(60), author: z.object({ id: z.string(), name: z.string() }).nullable(), content: CosmeticContent });

/** A club as everyone sees it (search, invitation page, leaderboards). */
export interface ClubSummary {
  id: string; name: string; tag: string; description: string; visibility: ClubVisibility; kit: ClubKit;
  members: number; createdAt: number;
}
export interface ClubMember {
  userId: string; name: string; color: string; look: Look | null; country: string | null; xp: number; rides: number; km: number; lastRideAt: number | null;
  role: ClubRole; since: number;
}
/** My club: the summary, the members and what I may do. The code goes to every member (they recruit). */
export interface ClubDetail extends ClubSummary { code: string; role: ClubRole; ownerId: string; roster: ClubMember[] }
export interface MyClubResponse { club: ClubDetail | null }
export interface ClubRankEntry { rank: number; clubId: string; name: string; tag: string; kit: ClubKit; members: number; value: number }
